import classNames from 'classnames/bind';
import { format, isValid } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Button, ButtonMode, CustomDatePicker, Divider, ICONS, Icon } from '@creditsights/styleguide-web';
import { FILTER_RELATIVE_DATE_RANGE } from '@creditsights/api-ui-web';
import styles from './DateRangeOpen.module.scss';

import { usePreviousValue, useWindowResizeListener } from '../../hooks/common';
import {
  DATE_FORMATS,
  INPUT_MASK,
  RANGE_INPUT_MASK,
  RANGE_SEPARATOR,
  RELATIVE_DATE_RANGE_LABELS,
} from './DateRange.contants';

import { isBeforeOrEqualIgnoringTime, parseDateRangeString } from './DateRange.logic';
import { DateType } from '../../Base.constants';

const cx = classNames.bind(styles);

interface DateRangeOpenProps {
  startDate: DateType;
  endDate: DateType;
  relativeRange: FILTER_RELATIVE_DATE_RANGE | null;
  onChange: (e: {
    startDate?: DateType;
    endDate?: DateType;
    relativeRange?: FILTER_RELATIVE_DATE_RANGE | null;
  }) => void;
  resetInput: number;
  setError: (data: boolean) => void;
}

const DateRangeOpen = ({
  startDate,
  endDate,
  relativeRange,
  onChange,
  resetInput,
  setError,
}: DateRangeOpenProps): React.ReactElement => {
  const [custom, setCustom] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [rawValue, setRawValue] = useState<string>(RANGE_INPUT_MASK);
  const previousResetInput = usePreviousValue(resetInput);
  const maskedDateInputRef = useRef<HTMLInputElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (resetInput !== 0) {
      setRawValue(RANGE_INPUT_MASK);
    }
  }, [resetInput]);

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleCaretClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation();
    toggleExpansion();
  };

  useEffect(() => {
    const shouldUseMask = resetInput !== previousResetInput;
    let start;
    let end;
    const isStartValid = startDate && isValid(startDate);
    const isEndValid = endDate && isValid(endDate);

    if (isStartValid) {
      start = format(startDate, DATE_FORMATS.MONTH_DAY_YEAR);
    } else {
      start = shouldUseMask || isEndValid ? INPUT_MASK : rawValue.slice(0, 8);
    }

    if (isEndValid) {
      end = format(endDate, DATE_FORMATS.MONTH_DAY_YEAR);
    } else {
      end = shouldUseMask || isStartValid ? INPUT_MASK : rawValue.slice(11);
    }

    const value = `${start}${RANGE_SEPARATOR}${end}`;
    setRawValue(value);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!open) {
      if (relativeRange || (startDate === null && endDate === null && rawValue === RANGE_INPUT_MASK)) {
        setCustom(false);
      } else if (startDate !== null || endDate !== null) {
        setCustom(true);
      }
      validateInput();
    }
  }, [open, startDate, endDate, relativeRange, rawValue]);

  const onResize = () => {
    if (!isMobile) {
      setOpen(false);
      maskedDateInputRef?.current?.blur();
    }
  };

  useWindowResizeListener(onResize);

  const onRelativeRangeSelect = (range: FILTER_RELATIVE_DATE_RANGE | null) => {
    onChange({ relativeRange: range });
    setOpen(false);
  };

  const onDatePickerEndSelect = (endDate: DateType) => {
    setError(false);
    const { isStartDateValid, parsedStartDate } = parseDateRangeString(rawValue);
    let start = isStartDateValid ? parsedStartDate : startDate;
    if (start && isBeforeOrEqualIgnoringTime(endDate!, start)) {
      start = endDate;
    }
    onChange({ startDate: start, endDate });
  };

  const onDatePickerStartSelect = (startDate: DateType) => {
    setError(false);
    const { isEndDateValid, parsedEndDate } = parseDateRangeString(rawValue);
    let end = isEndDateValid ? parsedEndDate : endDate;
    if (end && !isBeforeOrEqualIgnoringTime(startDate!, end)) {
      end = startDate;
    }
    onChange({ startDate, endDate: end });
  };

  const validateInput = () => {
    const { isRangeValid } = parseDateRangeString(rawValue);

    setError(!isRangeValid);
  };

  const renderPickers = () => {
    if (custom) {
      const { parsedStartDate, parsedEndDate } = parseDateRangeString(rawValue);
      return (
        <div className={cx('pickers-container')} data-testid="date-pickers-container">
          <Button
            mode={ButtonMode.PLAIN}
            className={cx('back-button')}
            onClick={() => setCustom(false)}
            dataTestId="date-range-back-button"
          >
            <Icon name={ICONS.ArrowLeft} height={12} width={12} />
            Standard Date Ranges
          </Button>
          <Divider />
          <div className={cx('pickers')}>
            <CustomDatePicker
              inputValue={parsedStartDate}
              selectsStart
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              onChange={onDatePickerStartSelect}
              maxDate={endDate || undefined}
              dataTestId="date-range-start-picker"
            />
            <CustomDatePicker
              inputValue={parsedEndDate}
              selectsEnd
              selected={endDate}
              startDate={startDate}
              endDate={endDate}
              onChange={onDatePickerEndSelect}
              minDate={startDate}
              dataTestId="date-range-end-picker"
            />
          </div>
        </div>
      );
    }
    return (
      <div className={cx('standard-range')} data-testid="date-range-popup">
        <div className={cx('options-wrapper')}>
          {Object.keys(FILTER_RELATIVE_DATE_RANGE).map((relativeRange) => (
            <Button
              mode={ButtonMode.PLAIN}
              className={cx('range-option')}
              dataTestId="standard-range-option"
              key={relativeRange}
              onClick={() => onRelativeRangeSelect(FILTER_RELATIVE_DATE_RANGE[relativeRange])}
            >
              {RELATIVE_DATE_RANGE_LABELS[relativeRange]}
            </Button>
          ))}
          <Button
            mode={ButtonMode.PLAIN}
            className={cx('range-option')}
            dataTestId="custom-range-option"
            onClick={() => {
              if (relativeRange === null && startDate === null && endDate === null) {
                setRawValue(RANGE_INPUT_MASK);
              }
              setCustom(true);
            }}
          >
            <span>Custom</span>
            <Icon name={ICONS.ArrowRight} height={12} width={12} />
          </Button>
        </div>
        <Divider className="divider" />
      </div>
    );
  };

  const handleClickInside = () => {
    toggleExpansion();
  };

  return (
    <div className={cx('body')} onClick={handleClickInside}>
      <div className={cx('date-heading')} data-testid="data-range-select">
        Date
        <span onClick={handleCaretClick}>
          <div className={isExpanded ? cx('expanded') : ''}>
            <Icon name={ICONS.CaretDown} height={8} width={8} />
          </div>
        </span>
      </div>
      {isExpanded && (
        <div onClick={(event) => event.stopPropagation()}>
          <div className={cx('dropdown-date-popup')}>{renderPickers()}</div>
        </div>
      )}
    </div>
  );
};

export default DateRangeOpen;
